import * as XLSX from 'xlsx-js-style'

export type PriceListExcelRow = {
    categoryName: string
    name: string
    supplierCode?: string
    price: number
}

type Params = {
    // Primera fila: título (ej. nombre de la empresa). Las siguientes: datos de cabecera (ej. ["Fecha", "1/10/2026"])
    headerRows: string[][]
    rows: PriceListExcelRow[]
    fileName: string
    showSupplierCode?: boolean
}

const compare = (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })

// Excel de lista de precios con formato: agrupado por categoría y ordenado por nombre.
// Lo usan la lista de precios (tipo de cliente) y la lista de precios congelada de un acopio.
export const writePriceListExcel = (params: Params) => {
    const { headerRows, rows, fileName, showSupplierCode } = params

    const columnLabels = showSupplierCode ?
        ["Categoría", "Código Interno", "Producto", "Precio"] :
        ["Categoría", "Producto", "Precio"]
    const priceColumn = columnLabels.length - 1
    const emptyRow = columnLabels.map(() => "")

    const sortedRows = [...rows].sort((a, b) => compare(a.categoryName, b.categoryName) || compare(a.name, b.name))

    const dataRows: (string | number)[][] = [
        ...headerRows,
        [],
        columnLabels
    ]
    const columnsHeaderRow = dataRows.length - 1

    let currentCategory: string | undefined
    const categoryRowIndexes: number[] = []

    sortedRows.forEach(row => {
        if (row.categoryName !== currentCategory) {
            currentCategory = row.categoryName
            categoryRowIndexes.push(dataRows.length)
            dataRows.push([currentCategory, ...emptyRow.slice(1)])
        }
        dataRows.push(showSupplierCode ?
            ["", row.supplierCode || "", row.name, row.price || 0] :
            ["", row.name, row.price || 0]
        )
    })

    const ws = XLSX.utils.aoa_to_sheet(dataRows)
    const range = XLSX.utils.decode_range(ws['!ref']!)

    for (let R = range.s.r; R <= range.e.r; ++R) {
        for (let C = range.s.c; C <= range.e.c; ++C) {
            const cellRef = XLSX.utils.encode_cell({ r: R, c: C })
            if (!ws[cellRef]) continue

            if (R === 0) {
                ws[cellRef].s = {
                    font: { bold: true, sz: 14, color: { rgb: "333333" } },
                    alignment: { horizontal: "left" }
                }
            }

            if (R === columnsHeaderRow) {
                ws[cellRef].s = {
                    font: { bold: true, color: { rgb: "000000" }, name: 'Arial', sz: 10 },
                    alignment: { horizontal: "center" },
                    fill: { fgColor: { rgb: "F2F2F2" } }
                }
            }

            if (categoryRowIndexes.includes(R)) {
                ws[cellRef].s = {
                    font: { bold: true, color: { rgb: "444444" } }
                }
            }

            if (C === priceColumn && R > columnsHeaderRow && !categoryRowIndexes.includes(R)) {
                ws[cellRef].t = 'n'
                ws[cellRef].z = '"$" #,##0.00'
                ws[cellRef].s = { alignment: { horizontal: "right" } }
            }
        }
    }

    ws['!cols'] = showSupplierCode ?
        [{ wch: 30 }, { wch: 18 }, { wch: 40 }, { wch: 18 }] :
        [{ wch: 30 }, { wch: 40 }, { wch: 18 }]

    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, "Precios")
    XLSX.writeFile(wb, fileName)
}
